import { FC } from 'react'
import { unstable_useBlocker as useBlocker, useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import {
  CategoryBarSection,
  Widget,
  WidgetSection,
  WidgetSimpleList,
} from '@factorialco/f0-react/dist/experimental'
import { Completed, DottedCircle, InProgressTask } from '@factorialco/f0-react/icons/app'
import { GoalsInsightDocument, GoalsInsightQuery } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import * as styles from './index.vanilla.css'

const prepareData = (data: GoalsInsightQuery) => {
  const employee = data.goals.employeesConnection?.nodes?.[0]

  const metricSummary = employee?.metricSummary
  const metrics = employee?.metrics ?? []

  const metricGroups = data.goals.metricGroupsConnection?.nodes ?? []

  const totalMetricGroups = data.goals.metricGroupsConnection?.totalCount ?? 0

  const goals = metricGroups.map((group) => {
    const inProgress = (group.summary?.averageProgress ?? 0) > 0

    const done = (group.summary?.averageProgress ?? 0) >= 100

    const needsAttention = !!group.summary?.needsAttention

    let icon = DottedCircle
    let iconClassName = 'text-f1-icon-secondary'

    if (done) {
      icon = Completed
      iconClassName = 'text-f1-icon-positive'
    } else if (inProgress) {
      icon = InProgressTask
      iconClassName = 'text-f1-icon-info'
    }

    return {
      id: group.id,
      title: group.title,
      icon,
      iconClassName,
      inProgress,
      done,
      alert: needsAttention
        ? {
            text: i18n.t('insights.widgets.goals.needsAttention'),
            level: 'warning' as const,
          }
        : undefined,
    }
  })

  const doneGoals = goals.filter((group) => group.done)
  const inProgressGoals = goals.filter((group) => group.inProgress && !group.done)
  const todoGoals = goals.filter((group) => !group.inProgress && !group.done)

  const items = [...doneGoals, ...inProgressGoals, ...todoGoals]

  const progress = metricSummary?.averageProgress ?? 0
  const needsAttention = !!metricSummary?.needsAttention
  const expectedProgress = metrics.length
    ? Math.round(metrics.reduce((acc, metric) => acc + metric.expectedProgress, 0) / metrics.length)
    : 0

  const showMoreCount = Math.max(totalMetricGroups - goals.length, 0)

  const isEmpty = !metricSummary || !items.length

  return {
    title: i18n.t('insights.widgets.goals.title'),
    progress,
    needsAttention,
    expectedProgress,
    items,
    buttonLabel: showMoreCount
      ? i18n.tp('insights.widgets.goals.see_more', { count: showMoreCount })
      : undefined,
    isEmpty,
  }
}

type PreparedData = ReturnType<typeof prepareData>

const GoalsWidgetComponent: FC<{
  data: PreparedData
  employeeId: number
  onClickWidget?: () => void
}> = ({
  data: { title, progress, needsAttention, expectedProgress, items, buttonLabel },
  employeeId,
  onClickWidget,
}) => {
  const { navigation } = useNavigation()

  const navigate = useNavigate()

  const linkUrl = navigation.goals.employees.employee.fullPath({ id: employeeId })

  const link = {
    title,
    url: linkUrl,
  }

  useBlocker(({ nextLocation }) => {
    if (linkUrl.endsWith(nextLocation.pathname)) {
      onClickWidget?.()
    }
    return false
  })

  const handleClickItem = (itemId: string | number) => {
    navigate(navigation.goals.groups.group.fullPath({ id: `${itemId}` }))
  }

  const handleNavigate = () => {
    navigate(linkUrl)
  }

  return (
    <Widget
      status={
        needsAttention
          ? {
              text: i18n.t('insights.widgets.goals.needsAttention'),
              variant: 'warning',
            }
          : undefined
      }
      header={{
        title,
        link,
      }}
      action={buttonLabel ? { label: buttonLabel, onClick: handleNavigate } : undefined}
    >
      <WidgetSection title={i18n.t('insights.widgets.goals.progress')}>
        <CategoryBarSection
          title={`${progress}%`}
          subtitle=""
          data={[
            {
              name: 'Progress',
              value: progress,
              color: 'categorical-1',
            },
            {
              name: 'Expected progress',
              value: 100 - progress,
              color: 'hsl(var(--neutral-20))',
            },
          ]}
          helpText={
            expectedProgress
              ? `${i18n.t('insights.widgets.goals.expectedProgress')}: ${expectedProgress}%`
              : undefined
          }
          hideTooltip
        />
      </WidgetSection>
      {items.length && (
        <Box className={styles.container}>
          <WidgetSimpleList items={items} onClickItem={handleClickItem} showAllItems />
        </Box>
      )}
    </Widget>
  )
}

export const buildGoalsWidget = () =>
  buildWidget({
    id: 'goals',
    document: GoalsInsightDocument,
    isEmpty: (data) => data.isEmpty,
    prepareData,
    component: GoalsWidgetComponent,
  })
